/**
 * View-as-user (impersonation) state.
 *
 * Single source of truth for whether the current tab is in view-as mode and
 * who's being viewed. Persisted in sessionStorage (this-tab only — other tabs
 * remain normal admin). Token + sensitive state never go to localStorage.
 *
 * Spec: docs/superpowers/specs/2026-04-24-view-as-user-impersonation-design.md
 */
import { getBackendURL, basename } from '../components/env';
import { clearAuthContext } from "./userEventTracker";

const KEYS = {
  active: 'imp:active',
  token: 'imp:token',
  target: 'imp:target',
  admin: 'imp:admin',
  auditId: 'imp:audit_id',
  origin: 'imp:origin',
  returnPending: 'imp:return_pending',
};

const _listeners = new Set();

function _notify() {
  for (const fn of _listeners) {
    try { fn(); } catch (_) {}
  }
}

export function isImpersonating() {
  try {
    return sessionStorage.getItem(KEYS.active) === '1';
  } catch (_) {
    return false;
  }
}

export function getImpersonationState() {
  if (!isImpersonating()) return null;
  try {
    return {
      token: sessionStorage.getItem(KEYS.token) || '',
      target: JSON.parse(sessionStorage.getItem(KEYS.target) || 'null'),
      admin: JSON.parse(sessionStorage.getItem(KEYS.admin) || 'null'),
      audit_id: sessionStorage.getItem(KEYS.auditId) || '',
      origin: JSON.parse(sessionStorage.getItem(KEYS.origin) || 'null'),
    };
  } catch (_) {
    return null;
  }
}

export function subscribeImpersonation(fn) {
  _listeners.add(fn);
  return () => _listeners.delete(fn);
}

export async function enterImpersonation({ targetId, originState, adminInfo }) {
  // adminInfo: { id, email, name, adminToken } — passed by caller from the current Supabase session.
  if (!targetId) throw new Error('enterImpersonation: targetId required');
  if (isImpersonating()) throw new Error('Already impersonating; exit first.');
  const adminToken = (adminInfo && adminInfo.adminToken) || '';
  const resp = await fetch(`${getBackendURL()}/api/admin/impersonate-user/`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(adminToken ? { Authorization: `Bearer ${adminToken}` } : {}),
    },
    body: JSON.stringify({ target_user_id: targetId }),
  });
  if (!resp.ok) {
    let body = '';
    try { body = await resp.text(); } catch (_) {}
    throw new Error(`impersonate-user failed: ${resp.status} ${body.slice(0, 200)}`);
  }
  const json = await resp.json();
  sessionStorage.setItem(KEYS.token, json.token);
  sessionStorage.setItem(KEYS.target, JSON.stringify(json.target || {}));
  sessionStorage.setItem(KEYS.admin, JSON.stringify({
    id: adminInfo?.id || '',
    email: adminInfo?.email || '',
    name: adminInfo?.name || '',
  }));
  sessionStorage.setItem(KEYS.auditId, json.audit_id);
  sessionStorage.setItem(KEYS.origin, JSON.stringify(originState || { path: '/admin-page' }));
  sessionStorage.setItem(KEYS.returnPending, JSON.stringify(originState?.modal_state || {}));
  sessionStorage.setItem(KEYS.active, '1');
  _notify();
  // Hard navigation so every component re-initializes against the new client.
  // Prefix the React Router basename so the URL resolves to a real route.
  try { clearAuthContext(); } catch (_) {}
  window.location.href = `${basename || ''}/schedules`;
}

export async function exitImpersonation() {
  const state = getImpersonationState();
  const token = state?.token;
  const auditId = state?.audit_id;
  // Origin paths are stored without the basename; prepend on exit.
  const rawOriginPath = state?.origin?.path || '/admin-page';
  const originPath = rawOriginPath.startsWith(basename || '')
    ? rawOriginPath
    : `${basename || ''}${rawOriginPath}`;
  // Best-effort backend close — never block exit on this.
  if (token && auditId) {
    try {
      await fetch(`${getBackendURL()}/api/admin/end-impersonation/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ audit_id: auditId }),
      });
    } catch (_) {}
  }
  // Clear all view-as keys EXCEPT imp:return_pending — that one survives the
  // navigation so the destination page (e.g. SuperAdmin) can consume it on
  // mount to re-open the analytics modal in the right state.
  for (const k of Object.values(KEYS)) {
    if (k === KEYS.returnPending) continue;
    try { sessionStorage.removeItem(k); } catch (_) {}
  }
  _notify();
  // Hard navigation back to the analytics origin.
  window.location.href = originPath;
}

// Test-only helpers — exposed for unit tests.
export function _writeStateForTests(s) {
  sessionStorage.setItem(KEYS.token, s.token || '');
  sessionStorage.setItem(KEYS.target, JSON.stringify(s.target || {}));
  sessionStorage.setItem(KEYS.admin, JSON.stringify(s.admin || {}));
  sessionStorage.setItem(KEYS.auditId, s.audit_id || '');
  sessionStorage.setItem(KEYS.origin, JSON.stringify(s.origin || {}));
  sessionStorage.setItem(KEYS.active, '1');
  _notify();
}

export function _clearStateForTests() {
  for (const k of Object.values(KEYS)) {
    try { sessionStorage.removeItem(k); } catch (_) {}
  }
  _notify();
}
